import type { PropsWithChildren } from 'react';
import React from 'react';
import cx from 'classnames';

import cypressIds from 'Components/cypress_ids.json';
import type { VehicleFeatures } from 'Pages/vehicles/helpers/vehicle.schema';
import { applyCypressData } from 'Utilities/helpers';

import { FeaturesManuallyAddedAdditionalFeatures } from '../FeaturesAdditionalSpec/FeaturesAdditionalSpec';
import { FeaturesEmptyState } from '../FeaturesEmptyState/FeaturesEmptyState';
import { FeaturesList } from '../FeaturesList/FeaturesList';

import { getFeatureDisplayStates } from './getFeatureDisplayStates';

import styles from './Features.module.scss';

interface FeaturesProps {
	className?: string;
	equipment: VehicleFeatures['equipment'];
	highValueCarFeatures: VehicleFeatures['highValueCarFeatures'];
	manuallyAddedAdditionalFeatures: VehicleFeatures['manuallyAddedAdditionalFeatures'];
}

const FeaturesContainer: React.FC<PropsWithChildren<{ className?: Nullable<string> }>> = ({ children, className }) => (
	<div className={cx(styles.container, className)}>
		<section className={styles.contentContainer} {...applyCypressData(cypressIds.containers.features)}>
			{children}
		</section>
	</div>
);

export const Features: React.FC<FeaturesProps> = ({
	className = null,
	equipment,
	highValueCarFeatures,
	manuallyAddedAdditionalFeatures,
}) => {
	const manuallyAddedAdditionalFeaturesTrimmed = manuallyAddedAdditionalFeatures
		?.trim?.()
		?.replace(/\n+/g, ', ')
		.replace(/,{2,}/g, ',');

	const { shouldShowEmptyState, shouldShowHighValueFeaturesList, shouldShowManuallyAddedAdditionalFeatures } =
		getFeatureDisplayStates({ equipment, highValueCarFeatures, manuallyAddedAdditionalFeaturesTrimmed });

	if (shouldShowEmptyState) {
		return (
			<FeaturesContainer className={className}>
				<FeaturesEmptyState />
			</FeaturesContainer>
		);
	}

	return (
		<FeaturesContainer className={className}>
			{shouldShowHighValueFeaturesList && (
				<FeaturesList
					classNames={{
						container: { [styles.removeBottomPadding]: shouldShowManuallyAddedAdditionalFeatures } as unknown as string,
						list: styles.list,
					}}
					equipment={equipment}
					highValueCarFeatures={highValueCarFeatures}
				/>
			)}
			{shouldShowManuallyAddedAdditionalFeatures && typeof manuallyAddedAdditionalFeaturesTrimmed === 'string' && (
				<FeaturesManuallyAddedAdditionalFeatures
					expanded
					manuallyAddedAdditionalFeatures={manuallyAddedAdditionalFeaturesTrimmed}
				/>
			)}
		</FeaturesContainer>
	);
};
